import React, { useRef, useState } from "react";
import { Upload, Loader2, ImagePlus, X } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable image uploader. Sends file to /api/uploads/image and returns a public URL.
 * Props: value (URL), onChange(url), aspect (e.g. "16/10")
 */
export default function ImageUpload({ value, onChange, aspect = "16/10", label = "Cover image" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${BACKEND_URL}${data.url}`;
      onChange(url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onSelect = (e) => upload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <div data-testid="image-upload">
      {label && <label className="label">{label}</label>}
      {value ? (
        <div className="relative card overflow-hidden" style={{aspectRatio: aspect}}>
          <img src={value} alt="cover" className="w-full h-full object-cover"/>
          <button
            type="button"
            onClick={()=>onChange("")}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur text-white hover:bg-rose-500 flex items-center justify-center"
            data-testid="image-clear-btn"
          ><X size={14}/></button>
          <button
            type="button"
            onClick={()=>inputRef.current?.click()}
            className="absolute bottom-2 right-2 px-3 py-1.5 text-xs rounded-full bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5"
          >
            <ImagePlus size={12}/> Replace
          </button>
        </div>
      ) : (
        <div
          onClick={()=>inputRef.current?.click()}
          onDragOver={(e)=>{e.preventDefault(); setDragOver(true);}}
          onDragLeave={()=>setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-blue-500 bg-blue-500/10" : "border-slate-700 hover:border-blue-500"}`}
          style={{aspectRatio: aspect, display:"flex", alignItems:"center", justifyContent:"center"}}
          data-testid="image-drop-zone"
        >
          {uploading ? (
            <div className="flex items-center gap-2 text-blue-400"><Loader2 className="animate-spin" size={18}/> Uploading…</div>
          ) : (
            <div className="text-center">
              <Upload className="mx-auto text-slate-400 mb-2" size={24}/>
              <div className="text-sm text-slate-300 font-semibold">Drop image here or click to upload</div>
              <div className="text-xs text-slate-500 mt-1">PNG, JPG, WebP up to 8 MB</div>
            </div>
          )}
        </div>
      )}
      <input
        ref={inputRef} type="file" accept="image/*"
        onChange={onSelect} className="hidden"
        data-testid="image-upload-input"
      />
      <div className="mt-2 flex items-center gap-2">
        <input
          value={value || ""}
          onChange={(e)=>onChange(e.target.value)}
          placeholder="Or paste image URL…"
          className="input flex-1 text-xs"
          data-testid="image-url-input"
        />
      </div>
    </div>
  );
}
